import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { listDimensionOptions } from "../api/variantDimensions";
import { resolveMaterialVariant } from "../api/materialVariants";

function normalizeDimensionKey(value) {
  return String(value || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/İ/g, "I")
    .replace(/Ş/g, "S")
    .replace(/Ğ/g, "G")
    .replace(/Ü/g, "U")
    .replace(/Ö/g, "O")
    .replace(/Ç/g, "C")
    .replace(/ı/g, "I")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function MaterialVariantSelectModal({
  materialId,
  materialName,
  templateDimensions,
  onClose,
  onResolved,
  optionalDimensionKeys = [],
  // Mevcut varyant değerleri: pencere bunlarla dolu açılır, kullanıcı yalnızca
  // değiştirmek istediği özelliği değiştirir. [{dimension_id, option_id|value_number|value_text}]
  initialSelections = null,
}) {
  const dims = useMemo(() => {
    const raw = Array.isArray(templateDimensions) ? templateDimensions : [];
    // Only dedupe exact duplicate dimension_id entries (safe).
    const seen = new Set();
    const out = [];
    for (const d of raw) {
      const dimId = Number(d?.dimension_id || d?.id);
      if (!Number.isFinite(dimId)) continue;
      if (seen.has(dimId)) continue;
      seen.add(dimId);
      out.push(d);
    }
    return out;
  }, [templateDimensions]);

  const grammageDupWarning = useMemo(() => {
    const raw = Array.isArray(templateDimensions) ? templateDimensions : [];
    const normalize = (s) => {
      const base = String(s || "").trim().toLocaleLowerCase("tr-TR");
      const decomp = base.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
      return decomp
        .replaceAll("ı", "i")
        .replaceAll("ğ", "g")
        .replaceAll("ş", "s")
        .replaceAll("ö", "o")
        .replaceAll("ü", "u")
        .replaceAll("ç", "c");
    };
    const isGrammage = (d) => {
      const k = normalize(d?.key);
      const l = normalize(d?.label);
      return k.includes("gramaj") || k.includes("grammage") || l.includes("gramaj") || l.includes("grammage");
    };
    const ids = new Set();
    for (const d of raw) {
      if (!isGrammage(d)) continue;
      const dimId = Number(d?.dimension_id || d?.id);
      if (Number.isFinite(dimId)) ids.add(dimId);
    }
    return ids.size > 1;
  }, [templateDimensions]);

  const optionalKeys = useMemo(
    () => new Set((optionalDimensionKeys || []).map(normalizeDimensionKey)),
    [optionalDimensionKeys]
  );

  function isRequired(d) {
    if (!d?.required) return false;
    const key = normalizeDimensionKey(d?.key);
    const label = normalizeDimensionKey(d?.label);
    return !optionalKeys.has(key) && !optionalKeys.has(label);
  }

  const [optionsByDimId, setOptionsByDimId] = useState({});
  const [selectionByDimId, setSelectionByDimId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pencere açıldığında mevcut seçimlerle doldurulur; verilmemişse boş başlar.
  useEffect(() => {
    const seed = {};
    for (const sel of Array.isArray(initialSelections) ? initialSelections : []) {
      const dimId = Number(sel?.dimension_id);
      if (!Number.isFinite(dimId) || dimId <= 0) continue;
      if (sel.option_id !== null && sel.option_id !== undefined) seed[dimId] = String(sel.option_id);
      else if (sel.value_number !== null && sel.value_number !== undefined) seed[dimId] = String(sel.value_number);
      else if (sel.value_text) seed[dimId] = String(sel.value_text);
    }
    setSelectionByDimId(seed);
  }, [dims, initialSelections]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const optionDims = dims.filter((d) => String(d?.value_type || "option") === "option");
        const entries = await Promise.all(
          optionDims.map(async (d) => {
            const dimId = Number(d.dimension_id || d.id);
            const opts = await listDimensionOptions(dimId);
            return [dimId, opts];
          })
        );
        if (cancelled) return;
        const next = {};
        for (const [dimId, opts] of entries) next[dimId] = opts;
        setOptionsByDimId(next);
      } catch (err) {
        console.error("VARIANT OPTIONS LOAD ERROR", err);
        setError("Varyant seçenekleri yüklenemedi.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dims]);

  async function submit() {
    setError("");
    const selections = [];
    for (const d of dims) {
      const dimensionId = Number(d.dimension_id || d.id);
      const valueType = String(d?.value_type || "option");
      const rawValue = selectionByDimId[dimensionId];

      if (valueType === "option") {
        if (rawValue) {
          selections.push({ dimension_id: dimensionId, option_id: Number(rawValue) });
        }
        continue;
      }

      if (valueType === "number") {
        const normalized = String(rawValue ?? "")
          .trim()
          .replace(",", ".");
        if (!normalized) {
          continue;
        }
        const numberValue = Number(normalized);
        if (Number.isFinite(numberValue)) {
          selections.push({ dimension_id: dimensionId, value_number: numberValue });
        } else {
          setError(`"${d.label}" için geçerli bir sayı girin.`);
          return;
        }
        continue;
      }

      const textValue = String(rawValue || "").trim();
      if (textValue) {
        selections.push({ dimension_id: dimensionId, value_text: textValue });
      }
    }

    const missingRequired = dims.filter((d) => isRequired(d)).some((d) => {
      const dimensionId = Number(d.dimension_id || d.id);
      const valueType = String(d?.value_type || "option");
      const rawValue = selectionByDimId[dimensionId];
      if (valueType === "option") return !rawValue;
      if (valueType === "number") {
        const normalized = String(rawValue ?? "")
          .trim()
          .replace(",", ".");
        return !normalized || !Number.isFinite(Number(normalized));
      }
      return !String(rawValue || "").trim();
    });

    if (missingRequired) {
      setError("Lütfen zorunlu varyant alanlarını seçin.");
      return;
    }

    if (!selections.length) {
      setError("En az 1 varyant seçimi yapın.");
      return;
    }

    try {
      const optionalDimensionIds = dims
        .filter((d) => d?.required && !isRequired(d))
        .map((d) => Number(d.dimension_id || d.id))
        .filter(Number.isFinite);
      const data = await resolveMaterialVariant({
        material_id: Number(materialId),
        selections,
        optional_dimension_ids: optionalDimensionIds,
      });
      onResolved({
        material_variant_id: data.material_variant_id,
        display_name: data.display_name,
        selections,
      });
      onClose();
    } catch (err) {
      console.error("MATERIAL VARIANT RESOLVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setError(`Varyant çözümlenemedi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    }
  }

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 20 }}>◇</Box>
            <Box><Typography variant="h6" fontWeight={800}>Varyant Seç</Typography><Typography variant="body2" sx={{ opacity: 0.8 }}>{materialName || "Seçili malzeme"}</Typography></Box>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
          {grammageDupWarning ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Bu malzemede “gramaj” varyantı birden fazla boyut olarak tanımlı görünüyor. Lütfen Malzemeler &gt; Varyant
              şablonunda tekilleştirin; aksi halde kaydetme sırasında zorunlu alan hatası alabilirsiniz.
            </Alert>
          ) : null}

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
          <Box sx={{ mb: 2 }}><Typography fontWeight={800}>Varyant özellikleri</Typography><Typography variant="body2" color="text.secondary">Zorunlu alanları doldurun; isteğe bağlı özellikleri ihtiyaç varsa seçin.</Typography></Box>
          {loading ? <Box sx={{ py: 4, textAlign: "center" }}><Typography variant="body2" color="text.secondary">Varyant seçenekleri yükleniyor...</Typography></Box> : (
            <Stack spacing={2} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>
              {dims.map((d) => {
              const dimId = Number(d.dimension_id || d.id);
              const valueType = String(d?.value_type || "option");
              const opts = optionsByDimId[dimId] || [];
              const value = selectionByDimId[dimId] || "";
              const label = `${d.label}${isRequired(d) ? " (zorunlu)" : ""}`;

              if (valueType === "option") {
                return (
                  <FormControl key={dimId} size="small" fullWidth>
                    <InputLabel id={`dim-${dimId}-label`}>{label}</InputLabel>
                    <Select
                      labelId={`dim-${dimId}-label`}
                      label={label}
                      value={value}
                      onChange={(e) =>
                        setSelectionByDimId((prev) => ({ ...prev, [dimId]: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>Seçiniz</em>
                      </MenuItem>
                      {opts.map((o) => (
                        <MenuItem key={o.id} value={String(o.id)}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }

              if (valueType === "number") {
                return (
                  <TextField
                    key={dimId}
                    size="small"
                    fullWidth
                    label={label}
                    value={value}
                    inputMode="decimal"
                    onChange={(e) =>
                      setSelectionByDimId((prev) => ({ ...prev, [dimId]: e.target.value }))
                    }
                  />
                );
              }

              return (
                <TextField
                  key={dimId}
                  size="small"
                  fullWidth
                  label={label}
                  value={value}
                  onChange={(e) =>
                    setSelectionByDimId((prev) => ({ ...prev, [dimId]: e.target.value }))
                  }
                />
              );
              })}
            </Stack>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">* Zorunlu varyant alanları tamamlanmadan kaydedilemez.</Typography>
        <Stack direction="row" spacing={1}><Button onClick={onClose} color="inherit" disabled={loading}>İptal</Button><Button onClick={submit} variant="contained" disabled={loading} sx={{ px: 2.5, borderRadius: 2, boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)" }}>{loading ? "Yükleniyor..." : "Varyantı Tamamla"}</Button></Stack>
      </DialogActions>
    </Dialog>
  );
}

export default MaterialVariantSelectModal;

